import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Logo } from "../Logo";
import api from "../../lib/api";
import {
  MailCheck,
  LayoutDashboard, FileText, ClipboardList, CreditCard, Users, Building2, BookOpen,
  Layers, CalendarDays, BarChart3, Settings as SettingsIcon, ScrollText, UserCog, CopyCheck,
  Bell, LogOut, Menu, X, Trash2, Wallet, Webhook, MessageSquare, MessageCircle,
} from "lucide-react";

const NAV = [
  { section: "Main", items: [
    { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/admin/pages", label: "Pages", icon: FileText },
    { to: "/admin/registrations", label: "Payment", icon: CreditCard },
    { to: "/admin/payment-verification", label: "Payment Verification", icon: MailCheck },
    { to: "/admin/students", label: "Students", icon: Users },
  ]},
  { section: "Management", items: [
    { to: "/admin/schools", label: "Schools", icon: Building2 },
  ]},
  { section: "Reporting", items: [
    { to: "/admin/reports", label: "Reports & Exports", icon: BarChart3 },
  ]},
  { section: "System", items: [
    { to: "/admin/whatsapp-inbox", label: "WhatsApp Inbox", icon: MessageCircle, badge: "inbox", testid: "nav-whatsapp-inbox" },
    { to: "/admin/whatsapp-bulk", label: "WhatsApp Bulk", icon: MessageSquare },
    { to: "/admin/settings", label: "Settings", icon: SettingsIcon },
    { to: "/admin/account-settings", label: "Account Settings", icon: Wallet },
    { to: "/admin/webhooks", label: "Webhooks", icon: Webhook },
    { to: "/admin/users", label: "Admin Users", icon: UserCog },
    { to: "/admin/duplicates", label: "Duplicates", icon: CopyCheck, badge: "dups", testid: "nav-duplicates" },
    { to: "/admin/audit-logs", label: "Audit Logs", icon: ScrollText },
    { to: "/admin/bin", label: "Bin", icon: Trash2 },
  ]},
];

function SidebarContent({ onNav, badges = {} }) {
  return (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5 border-b border-slate-800">
        <Logo light />
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-6">
        {NAV.map((group) => (
          <div key={group.section}>
            <div className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{group.section}</div>
            <div className="space-y-1">
              {group.items.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  onClick={onNav}
                  data-testid={it.testid || `nav-${it.label.toLowerCase().split(" ")[0]}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      isActive ? "bg-blue-600 text-white shadow-lg shadow-blue-600/20" : "text-slate-300 hover:bg-slate-800 hover:text-white"
                    }`
                  }
                >
                  <it.icon className="w-[18px] h-[18px]" />
                  <span className="flex-1">{it.label}</span>
                  {it.badge && badges[it.badge] > 0 && <span className={`min-w-[20px] h-5 px-1.5 rounded-full ${it.badge === "dups" ? "bg-amber-500" : "bg-[#25d366]"} text-white text-[11px] font-bold flex items-center justify-center`} data-testid={it.badge === "inbox" ? "nav-inbox-unread" : "nav-dups-badge"}>{badges[it.badge]}</span>}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>
    </div>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notif, setNotif] = useState({ items: [], unread: 0 });
  const [showNotif, setShowNotif] = useState(false);

  const [badges, setBadges] = useState({});
  const loadNotif = () => api.get("/notifications").then((r) => setNotif(r.data)).catch(() => {});
  const loadBadges = () => Promise.all([api.get("/wa-inbox/unread").catch(() => null), api.get("/duplicates/count").catch(() => null)])
    .then(([i, d]) => setBadges({ inbox: i?.data.unread_chats, dups: d?.data.count }));
  useEffect(() => { loadNotif(); const t = setInterval(loadNotif, 30000); return () => clearInterval(t); }, []);
  useEffect(() => { loadBadges(); const t = setInterval(loadBadges, 20000); return () => clearInterval(t); }, []);

  const openNotif = async () => {
    setShowNotif((s) => !s);
    if (!showNotif && notif.unread) { await api.post("/notifications/read"); loadNotif(); }
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="w-64 flex-shrink-0 bg-slate-900 hidden md:block sticky top-0 h-screen">
        <SidebarContent badges={badges} />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-64 bg-slate-900">
            <button className="absolute right-3 top-4 text-slate-400" onClick={() => setMobileOpen(false)}><X className="w-5 h-5" /></button>
            <SidebarContent badges={badges} onNav={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200 px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button className="md:hidden text-slate-600" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn"><Menu className="w-6 h-6" /></button>
            <div className="md:hidden"><Logo size="sm" /></div>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <button onClick={openNotif} data-testid="notifications-btn" className="relative w-10 h-10 rounded-xl hover:bg-slate-100 flex items-center justify-center text-slate-600">
                <Bell className="w-5 h-5" />
                {notif.unread > 0 && <span className="absolute top-1 right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[9px] flex items-center justify-center font-bold">{notif.unread > 99 ? "99+" : notif.unread}</span>}
              </button>
              {showNotif && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl border border-slate-200 shadow-xl overflow-hidden z-40">
                  <div className="px-4 py-3 border-b border-slate-100 font-semibold text-sm text-slate-700">Notifications</div>
                  <div className="max-h-80 overflow-y-auto">
                    {notif.items.length === 0 && <div className="px-4 py-6 text-sm text-slate-400 text-center">No notifications</div>}
                    {notif.items.map((n) => (
                      <div key={n.id} onClick={() => { if (n.phone) { setShowNotif(false); navigate(`/admin/whatsapp-inbox?chat=${n.phone}`); } }} className={`px-4 py-3 border-b border-slate-50 text-sm text-slate-600 hover:bg-slate-50 ${n.phone ? "cursor-pointer" : ""}`}>{n.message}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2.5 pl-2 border-l border-slate-200">
              <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-semibold text-sm">
                {(user?.name || "A").charAt(0).toUpperCase()}
              </div>
              <div className="hidden sm:block leading-tight">
                <div className="text-sm font-semibold text-slate-800">{user?.name}</div>
                <div className="text-xs text-slate-400">{user?.role}</div>
              </div>
              <button onClick={() => { logout(); navigate("/login"); }} data-testid="logout-btn" className="ml-1 w-10 h-10 rounded-xl hover:bg-rose-50 flex items-center justify-center text-slate-500 hover:text-rose-600 transition-colors">
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8" onClick={() => showNotif && setShowNotif(false)}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
